import React from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { getBadgeImage } from 'images/RelicVault/badges';
import ShimmerHalo from '../shared/ShimmerHalo';
import { BadgeShieldIcon, CheckIcon, CloseIcon } from '../shared/RelicVaultIcons';
import { useRelicVaultLayout } from '../shared/useRelicVaultLayout';
import {
  COLORS,
  bodyFont,
  em,
  headingFont,
  modalBackdrop,
  modalSurface,
} from '../shared/theme';
import { getBadge } from 'config/relicVault/badges';
import { tierByKey } from 'utils/relicVault/relicDisplay';
import { iconGlowFor } from 'utils/relicVault/colorUtils';
import { IS_WEB } from 'utils/relicVault/webStyle';

const CARD_WIDTH = 380;
const ART_SIZE = 210;

/**
 * Badge detail popup. Like the reference, it leads with the badge itself and
 * says how to earn it; the requirement text comes straight from the badge sheet.
 */
const BadgeDetailModal = ({ badgeId, owned, onClose }) => {
  const { width, isPhone } = useRelicVaultLayout();
  const badge = badgeId ? getBadge(badgeId) : null;
  if (!badge) return null;

  const tier = tierByKey(badge.rarity);
  const rarityColor = tier?.color ?? '#4ADE80';
  const image = getBadgeImage(badge.id);
  const cardWidth = isPhone ? width * 0.92 : Math.min(CARD_WIDTH, width - 48);

  return (
    <View style={[StyleSheet.absoluteFill, styles.backdrop, modalBackdrop]}>
      <TouchableOpacity
        activeOpacity={1}
        style={StyleSheet.absoluteFill}
        onPress={onClose}
        accessibilityLabel="Close"
      />
      <View
        style={[styles.card, modalSurface, { width: cardWidth }, isPhone && styles.cardPhone]}>
        <TouchableOpacity
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close"
          style={styles.closeBtn}>
          <CloseIcon />
        </TouchableOpacity>

        <View
          style={[
            styles.art,
            owned && image
              ? IS_WEB
                ? { backgroundImage: iconGlowFor(rarityColor) }
                : { backgroundColor: `${rarityColor}22` }
              : { backgroundColor: 'rgba(255,255,255,0.04)' },
          ]}>
          {image ? (
            <Image
              source={image}
              resizeMode="contain"
              style={[
                styles.artImage,
                !owned &&
                  (IS_WEB
                    ? { filter: 'grayscale(1) brightness(0.55)', opacity: 0.85 }
                    : { opacity: 0.32 }),
              ]}
            />
          ) : (
            /* Still being drawn by the designer. */
            <View style={styles.artPending}>
              <BadgeShieldIcon size={40} color="rgba(201,193,240,0.5)" />
            </View>
          )}
          <ShimmerHalo
            glow={owned && image ? `${rarityColor}80` : 'rgba(255,255,255,0.25)'}
            radius={18}
            haloSize={30}
            haloSpread={5}
            replayKey={badge.id}
            delay={100}
          />
        </View>

        <View style={[styles.rarityPill, { borderColor: rarityColor }]}>
          <Text style={[styles.rarityText, { color: rarityColor }]}>
            {tier?.label ?? badge.rarity} Badge
          </Text>
        </View>

        <Text style={styles.name}>{badge.name}</Text>
        <Text style={styles.category}>
          {badge.category}
          {!!badge.guild && ` · ${badge.guild}`}
        </Text>

        <Text style={styles.howToLabel}>How to earn it</Text>
        <Text style={styles.requirement}>{badge.requirement}</Text>

        {owned ? (
          <View style={[styles.status, styles.statusOwned]}>
            <CheckIcon size={12} />
            <Text style={[styles.statusText, { color: COLORS.green }]}>Earned</Text>
          </View>
        ) : (
          <View style={[styles.status, styles.statusLocked]}>
            <Text style={[styles.statusText, { color: COLORS.muted }]}>Not Earned Yet</Text>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    zIndex: 1000,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.14)',
    borderRadius: 24,
    paddingTop: 32,
    paddingHorizontal: 28,
    paddingBottom: 28,
    alignItems: 'center',
  },
  cardPhone: {
    paddingTop: 24,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  closeBtn: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
  },
  art: {
    width: ART_SIZE,
    maxWidth: '100%',
    aspectRatio: 1,
    borderRadius: 18,
    marginBottom: 14,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  artImage: {
    width: '86%',
    height: '86%',
  },
  artPending: {
    width: '42%',
    aspectRatio: 1,
    borderRadius: 999,
    backgroundColor: 'rgba(6,5,18,0.9)',
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: 'rgba(255,255,255,0.16)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rarityPill: {
    borderWidth: 2,
    borderRadius: 999,
    paddingVertical: 5,
    paddingHorizontal: 14,
    marginBottom: 10,
  },
  rarityText: {
    ...headingFont('800'),
    fontSize: 11,
    letterSpacing: em(0.04, 11),
    textTransform: 'uppercase',
  },
  name: {
    ...headingFont('800'),
    fontSize: 20,
    color: COLORS.textWarm,
    textAlign: 'center',
  },
  category: {
    ...bodyFont('600'),
    fontSize: 12.5,
    color: COLORS.muted,
    marginTop: 6,
    textAlign: 'center',
  },
  howToLabel: {
    ...bodyFont('800'),
    fontSize: 10.5,
    letterSpacing: em(0.12, 10.5),
    textTransform: 'uppercase',
    color: COLORS.dim,
    marginTop: 16,
  },
  requirement: {
    ...bodyFont('400'),
    fontSize: 14,
    lineHeight: 21,
    color: COLORS.lilacSoft,
    marginTop: 6,
    textAlign: 'center',
  },
  status: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 18,
    paddingVertical: 7,
    paddingHorizontal: 16,
    borderRadius: 999,
    borderWidth: 1.5,
  },
  statusOwned: {
    backgroundColor: 'rgba(74,222,128,0.14)',
    borderColor: 'rgba(74,222,128,0.4)',
  },
  statusLocked: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderColor: 'rgba(255,255,255,0.12)',
  },
  statusText: {
    ...headingFont('800'),
    fontSize: 11.5,
    marginLeft: 6,
  },
});

export default BadgeDetailModal;
